import React, { useEffect, useState, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { showToast } from '@modules/toasts/features/toastsSlice';
import styles from './blockly.mod.scss'
import * as Blockly from 'blockly/core';
import * as generators from '../features/generators';
import * as lang from '../features/lang/index';
import 'blockly/blocks';
import { temaPara } from '../features/utils/theme';
import '../features/blocks/index';
import '../features/renderers/renderer';
import '../features/renderers/seniorRenderer';
import '../features/renderers/horizontalFlyout';
// Además de lo importado, registra las categorías con las clases del toolbox legacy (side effect).
import { applyLegacyToolboxClasses, armTrashcanOnBlockDrag, injectToolboxTrashcan } from '../features/utils/toolboxCategory';
import { PinsDB } from '../features/utils/pinsDB';
import { VariablesDynamicEducabot } from '../features/blocks/variablesDynamic/variableBlocks';
import { ProceduresDynamicEducabot } from '../features/blocks/proceduresDynamic/procedureBlocks';
import { VariablesJavascriptDynamicEducabot } from '../features/blocks/variablesJavascriptDynamic/variableBlocks';
import * as profiles from '../features/profiles';
import boardTypes from '@modules/boards/features/constants';
import { getLocale } from '../../../config/locale';
import { useUpdateBlockly, useBlockly } from '../features/hooks';
import { startLoading, finishLoading } from '../features/blocklySlice';
import { resizeWorkspace, keepHorizontalFlyoutOffset } from '../features/utils/utils';
import { registerBlockHelpItem } from '../features/utils/blockHelp';
import { registerLegacyFlyoutLabel } from '../features/utils/flyoutLabel';
import { getRightPanelData } from '../../../actions/rightPanelAction';
// userMarker instala workspace.markBlock/block.setUsermarker como side effect al importarse.
import WorkSpaceClient from '../features/collaboration/workSpaceClient';
import '../features/collaboration/userMarker';
import '../features/utils/orphanedConnection';
// eslint-disable-next-line import/no-webpack-loader-syntax, import/order
import cssContent from 'css-loader?{"url":false}!../../../helpers/popups/popups.css';


const BlocklyContainer = (props) => {
  const blocklyDiv = useRef();
  const blocklyState = useRef();
  const primaryWorkspace = useRef();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [blocklyProject, setBlocklyProject] = useState({});
  blocklyState.current = useBlockly();
  useUpdateBlockly(blocklyProject);

  // console.log('=========props', props.tabInfo, blocklyState.project);
  const { darkTheme, tabInfo, socket, userData, teamList } = props;
  const blocklyGenerator = generators[props.tabInfo?.board?.lang || 'JavaScript'].default;
  const blocklyLocale = lang[getLocale().locale];
  const blocklyProfile = profiles.default[props.tabInfo?.board?.profile || ''];
  const juniorBlocksHorizontal = (props.tabInfo?.board?.type === boardTypes.JUNIOR && props.tabInfo?.board?.toolboxLayout === 'horizontal');
  const juniorBlocks = (props.tabInfo?.board?.type === boardTypes.JUNIOR);

  const blocklyOptions = {
    readOnly: false,
    // Sin tachito ni controles de zoom nativos: el shell trae su propia botonera.
    trashcan: false,
    media: `${process.env.IS_ELECTRON ? '.' : ''}/images/media/`,
    // Los perfiles horizontales (junior chico, ej. micro:bit) van sin scrollbar de workspace, como prod.
    move: {
      scrollbars: juniorBlocksHorizontal ? false : {
        horizontal: true,
        vertical: true,
      },
      drag: true,
      wheel: true,
    },
    zoom: {
      controls: false,
      wheel: true,
      startScale: juniorBlocks ? 1.8 : 0.8,
      maxScale: 3,
      minScale: 0.3,
      scaleSpeed: 1.2,
      pinch: true,
    },
    comments: false,
    horizontalLayout: juniorBlocksHorizontal,
    toolboxPosition: juniorBlocksHorizontal ? 'end' : 'start',
    // El GAP_X default de v13 (24) separa de más el flyout horizontal respecto de prod (16px).
    ...(juniorBlocksHorizontal ? { plugins: { flyoutsHorizontalToolbox: 'juniorHorizontalFlyout' } } : {}),
    toolbox: ReactDOMServer.renderToString(blocklyProfile.categoryXml),
    initialXml: blocklyProfile.initXml,
  };

  const updateBlocklyProject = (e) => {
    const newCode = generateCode();
    const savedBlocklyProject = blocklyState.current.project;

    const withWorkspace = JSON.stringify(Blockly.serialization.workspaces.save(primaryWorkspace.current));

    if (primaryWorkspace.current && withWorkspace !== savedBlocklyProject.withWorkspace) {
      let canvas = primaryWorkspace.current.svgBlockCanvas_.cloneNode(true);
      canvas.removeAttribute('transform');
      canvas = new XMLSerializer().serializeToString(canvas);
      const canvasBBox = document.getElementsByClassName('blocklyBlockCanvas')[0]?.getBBox();

      const newData = {};
      newData.withWorkspace = withWorkspace;
      newData.canvas = canvas;
      newData.canvasBBoxX = canvasBBox?.x || 0;
      newData.canvasBBoxY = canvasBBox?.y || 0;
      newData.canvasBBoxWidth = canvasBBox?.width || 0;
      newData.canvasBBoxHeight = canvasBBox?.height || 0;
      newData.canvasCode = newCode;
      newData.port = props.tabInfo?.port || '';
      newData.code = newCode;
      setBlocklyProject(newData);

      if (props.setWorkspace) {
        props.setWorkspace(primaryWorkspace.current);
      }
    }
  };

  const generateCode = () => {
    const code = blocklyGenerator.workspaceToCode(primaryWorkspace.current);
    return code;
  };

  // canvasCss y blocksCss alimentan las miniaturas de los pares en colaboración. Se calcula una vez:
  // el CSS solo depende de perfil y tema, fijos al inicio.
  const updateBlocklyCss = () => {
    const cssBlockly = Array.from(document.querySelectorAll('style'))
      .filter((style) => style.textContent.includes('.blocklyDraggable'))
      .map((style) => style.textContent)
      .join('');
    setBlocklyProject({
      canvasCss: `<defs><style type="text/css" xmlns="http://www.w3.org/1999/xhtml"><![CDATA[${cssBlockly}]]></style></defs>`,
      blocksCss: `<defs><style type="text/css" xmlns="http://www.w3.org/1999/xhtml"><![CDATA[${cssContent}]]></style></defs>`,
    });
  };


  useEffect(() => {
    Blockly.setLocale(blocklyLocale);
  }, []);

  // En colaboración no se reinyecta al cambiar el guardado (`broadcastCollabData`): el contenido va por
  // eventos y recrear el canvas deja al cliente de colaboración atado al workspace viejo.
  const reinjectKey = tabInfo?.collaborationProject ? null : tabInfo?.withWorkspace;

  useEffect(() => {
    // Loader solo en colaboración, para no cambiar ML/Creabots.
    let loaderTimeout;
    if (tabInfo?.collaborationProject) {
      dispatch(startLoading());
      loaderTimeout = setTimeout(() => dispatch(finishLoading()), 800);
    }
    const { initialXml, ...rest } = blocklyOptions;
    // Va antes del inject: el flyout se arma con los inflaters registrados.
    registerLegacyFlyoutLabel();
    primaryWorkspace.current = Blockly.inject(blocklyDiv.current, {
      // 'senior' es zelos con el alto de fila ajustado a prod (ver renderers/seniorRenderer.js).
      renderer: juniorBlocks ? 'junior' : 'senior',
      theme: temaPara(darkTheme),
      ...rest,
    });

    // Las dos clases de contenedor que el SCSS del shell usa como scope y que
    // no salen del cssConfig de la categoria (ver utils/toolboxCategory.js).
    applyLegacyToolboxClasses(primaryWorkspace.current);

    // Markup propio de la papelera (div/span/i): sin esto el CSS de abajo no
    // tiene sobre que pintar el icono abierto/cerrado (ver utils/toolboxCategory.js).
    injectToolboxTrashcan(primaryWorkspace.current);

    updateBlocklyCss();

    const onBlockDrag = armTrashcanOnBlockDrag(primaryWorkspace.current);
    primaryWorkspace.current.addChangeListener(onBlockDrag);

    new PinsDB(primaryWorkspace.current, props.tabInfo?.board || null);

    if (initialXml) {
      primaryWorkspace.current.clear();
      // Carga robusta: detecta XML viejo / JSON nuevo / vacio; ante error cae al proyecto inicial y avisa al usuario.
      const { tabInfo } = props;
      const saved = tabInfo?.withWorkspace;
      const loadInitial = () => Blockly.serialization.workspaces.load(initialXml, primaryWorkspace.current);
      try {
        if (typeof saved === 'string' && saved.trim().startsWith('<')) {
          Blockly.Xml.domToWorkspace(Blockly.utils.xml.textToDom(saved), primaryWorkspace.current);
        } else if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
          Blockly.serialization.workspaces.load(saved, primaryWorkspace.current);
        } else if (typeof saved === 'string' && saved.trim().startsWith('{')) {
          Blockly.serialization.workspaces.load(JSON.parse(saved), primaryWorkspace.current);
        } else {
          loadInitial();
        }
      } catch (error) {
        console.error('No se pudo cargar el proyecto guardado; se abre el proyecto inicial.', error);
        dispatch(showToast({
          type: 'error',
          // La clave lleva la raíz 'blocks' de es.json: flat() la conserva al aplanar.
          message: intl.formatMessage({ id: 'blocks.blockly.projectLoadError' }),
          options: { autoClose: true },
        }));
        primaryWorkspace.current.clear();
        try {
          loadInitial();
        } catch (fallbackError) {
          console.error('No se pudo cargar el proyecto inicial.', fallbackError);
        }
      }
    }

    const containerId = `blocklyContainer-${tabInfo?.id || 'id'}`;
    // Se corre solo el grupo del flyout, no el contenedor, para liberar la esquina del botón de
    // ejecutar horizontal (148px): correr el contenedor también movería el bloque inicial.
    const onresize = () => {
      resizeWorkspace(primaryWorkspace.current, containerId);
      if (juniorBlocksHorizontal) {
        keepHorizontalFlyoutOffset(primaryWorkspace.current, 148);
        // Sin scrollbars Blockly centra el contenido; se fuerza (0,0) para que el bloque inicial
        // quede anclado a la izquierda como en prod.
        primaryWorkspace.current?.scroll(0, 0);
      }
    };

    // ResizeObserver y no el resize de window: `.blocklyPanel` anima su alto (transition) y en el
    // evento `offsetHeight` todavía da el valor viejo. También cubre cambios de layout sin resize de
    // ventana (panel derecho, `.blocklyPanel-half`, `.blocklyPanel-collab`).
    const container = document.getElementById(containerId);
    const observed = container?.parentElement;
    const observer = observed ? new ResizeObserver(onresize) : null;
    if (observer) observer.observe(observed);

    // Cubre el caso de que el contenedor todavía no exista cuando corre este efecto.
    window.addEventListener('resize', onresize, false);
    onresize();

    primaryWorkspace.current.addChangeListener(generateCode);
    primaryWorkspace.current.addChangeListener(updateBlocklyProject);
    // No agregar `Blockly.Events.disableOrphans`: en prod los bloques sueltos no se deshabilitan, y
    // un bloque deshabilitado deja de generar código.

    // Se avisa apenas inicializa: si no, el consumidor no recibe el workspace hasta que se toque un bloque.
    const { setWorkspace: notifyWorkspace } = props;
    if (notifyWorkspace) {
      notifyWorkspace(primaryWorkspace.current);
    }
    // Las clases de ML no son del proyecto: las deja acá el panel de ML y AI_bluethooth_control arma
    // un input por cada una. Sin modelo cargado queda solo el encabezado, igual que en prod.
    primaryWorkspace.current.getMLClasses = () => {
      const ml = primaryWorkspace.current.ML;
      if (!ml || !ml.classNames) return [];

      return Object.keys(ml.classNames).map((key) => [ml.classNames[key], key]);
    };

    primaryWorkspace.current.setMLClasses = (classNames = {}) => {
      primaryWorkspace.current.ML = { classNames };

      primaryWorkspace.current.getAllBlocks(false).forEach((block) => {
        if (typeof block.updateClassNames === 'function') block.updateClassNames();
      });
      Blockly.svgResize(primaryWorkspace.current);
    };


    primaryWorkspace.current.registerToolboxCategoryCallback('VARIABLE_DYNAMIC', VariablesDynamicEducabot.flyoutCategory);
    primaryWorkspace.current.registerToolboxCategoryCallback('PROCEDURES_DYNAMIC', ProceduresDynamicEducabot.flyoutCategory);

    // Los perfiles JavaScript (micro:bit / FIFA) piden estas categorías con otro nombre. Las
    // variables emiten otro código y tienen tipos propios; los procedimientos reusan el builder.
    primaryWorkspace.current.registerToolboxCategoryCallback('VARIABLE_JAVASCRIPT_DYNAMIC', VariablesJavascriptDynamicEducabot.flyoutCategory);
    primaryWorkspace.current.registerToolboxCategoryCallback('PROCEDURE_JAVASCRIPT', ProceduresDynamicEducabot.flyoutCategory);

    /**
     * Disable context menu items
     * Blockly.ContextMenuRegistry.registry.unregister('someID');
     * find the ids at: https://github.com/google/blockly/blob/0e22a7982ee99f9efd258c68826806189729d096/core/contextmenu_items.ts
     */

    // Va después del inject: Blockly registra ahí sus items por defecto y el propio tiene que quedar
    // arriba del nativo.
    registerBlockHelpItem((tutorialCodename, workspaceId) => {
      dispatch(getRightPanelData({ tutorialCodename, workspaceId, tabId: null }));
    });

    return () => {
      if (loaderTimeout) clearTimeout(loaderTimeout);
      if (tabInfo?.collaborationProject) dispatch(finishLoading());
      primaryWorkspace.current.dispose();
      primaryWorkspace.current.removeChangeListener(generateCode);
      primaryWorkspace.current.removeChangeListener(updateBlocklyProject);
      primaryWorkspace.current.removeChangeListener(onBlockDrag);
      window.removeEventListener('resize', onresize, false);
      if (observer) observer.disconnect();
    };

  // `board?.id` y no `board`: en /colaboracion getProject() reconstruye tabInfo cada segundo y un
  // objeto nuevo recrearía el workspace entero.
  }, [reinjectKey, tabInfo?.board?.id]);

  // El tema se cambia en caliente y no en el efecto de arriba: recrear el canvas por un cambio de
  // tema perdería el proyecto en pantalla.
  useEffect(() => {
    if (primaryWorkspace.current) primaryWorkspace.current.setTheme(temaPara(darkTheme));
  }, [darkTheme]);


  // Efecto aparte con `socket` en las deps: el socket llega asíncrono y el efecto de inyección ya
  // corrió con null. Solo perfiles senior con proyecto/sesión de colaboración.
  useEffect(() => {
    if (!primaryWorkspace.current || tabInfo?.board?.type !== boardTypes.SENIOR || !socket) {
      return undefined;
    }
    if (!tabInfo?.collaborationProject || !tabInfo?.collaborationSession) {
      return undefined;
    }

    const workspaceClient = new WorkSpaceClient(
      socket,
      Blockly,
      primaryWorkspace.current,
      userData?.userId,
      tabInfo.collaborationSession,
    );
    let changeListener = null;
    workspaceClient.start().then(() => {
      workspaceClient.setUsersList(teamList || []);
      changeListener = (event) => {
        // Solo se sincroniza contenido: los eventos de UI (`isUiEvent` en v13) rompen la
        // deserialización del otro lado.
        if (typeof event.type === 'undefined' || event.isUiEvent) {
          return;
        }
        workspaceClient.handleUnmarkPosition(event);
        workspaceClient.addEvent(event);
        if (!Blockly.Events.getGroup()) {
          workspaceClient.flushEvents();
        }
      };
      primaryWorkspace.current.addChangeListener(changeListener);
    });

    return () => {
      if (changeListener) {
        primaryWorkspace.current?.removeChangeListener(changeListener);
      }
      // Suelta los listeners del socket compartido: si no se apilan y cada evento se aplica de más.
      workspaceClient.dispose();
    };
  }, [socket, tabInfo?.collaborationProject, tabInfo?.collaborationSession, tabInfo?.board?.type]);


  return (
    <div id={`blocklyContainer-${props.tabInfo?.id || 'id'}`} ref={blocklyDiv} className={styles.blockly} />
  );
}

export default BlocklyContainer;
